import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
// Импортируем скалярные типы
import { typeDefs } from "./scalars";
import { routeAbortLink } from "../../config/apollo-route-abort";
import { bugsnagClient } from "../../config/bugsnag";
import {
  activeAccountId,
  dropActiveAccount,
  leaveForbiddenApp,
} from "../../store/accountStore";
import { triggerGlobalNotifications } from "../../context/ErrorBridge";

const httpLink = createHttpLink({
  uri: `${process.env.REACT_APP_GRAPHQL_URI}`,
  credentials: "include",
  fetchOptions: {
    mode: "cors",
  },
});

console.log("GraphQL URI:", process.env.REACT_APP_GRAPHQL_URI);

// Создаем промежуточное звено для обработки ответов сервера
const bigIntMiddleware = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    // Если в response есть BigInt значения, преобразуем их в строки
    if (response.data) {
      const processData = (data: any): any => {
        if (!data) return data;

        if (typeof data === "object" && data !== null) {
          // Обрабатываем объекты и массивы
          Object.keys(data).forEach((key) => {
            const value = data[key];

            // Если значение - BigInt
            if (typeof value === "bigint") {
              data[key] = String(value);
            }
            // Если это объект или массив, рекурсивно обрабатываем его
            else if (typeof value === "object" && value !== null) {
              processData(value);
            }
          });
        }

        return data;
      };

      response.data = processData(response.data);
    }
    return response;
  });
});

/**
 * The account being worked in: the user's own, or one they were invited into.
 * Without the header the backend answers about the user's own account, and the
 * header alone grants nothing — membership is checked on every request.
 */
const accountLink = new ApolloLink((operation, forward) => {
  const accountId = activeAccountId();
  if (accountId) {
    operation.setContext(
      ({ headers = {} }: { headers?: Record<string, string> }) => ({
        headers: { ...headers, "x-account-id": String(accountId) },
      }),
    );
  }
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError, operation }): any => {
  if (networkError?.name === "RouteChangeAbortError") {
    // Silently handle route change aborts
    return;
  }

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      // Access to the picked account was withdrawn: fall back to the own one
      // instead of failing every request from here on.
      if (err.extensions?.reason === "ACCOUNT_ACCESS") {
        dropActiveAccount();
        return;
      }

      // An app outside the teammate's App Access. A page that was loading it
      // has nothing to show and goes to My Apps; a button that tried to change
      // it just says why it did nothing.
      if (err.extensions?.reason === "APP_ACCESS") {
        triggerGlobalNotifications({
          type: "error",
          data: { message: err.message, placement: "top" },
        });
        const isQuery = operation.query.definitions.some(
          (d: any) =>
            d.kind === "OperationDefinition" && d.operation === "query",
        );
        if (isQuery) leaveForbiddenApp();
        return;
      }

      switch (err.extensions.code) {
        case "UNAUTHENTICATED":
          if (
            window.location.pathname === "/reset-password" ||
            window.location.pathname === "/recover-password" ||
            window.location.pathname === "/login" ||
            window.location.pathname === "/registration" ||
            window.location.pathname === "/admin-login-to-client"
          ) {
            return null;
          }
          return (window.location.href =
            `${process.env.REACT_APP_FRONTEND_WEB_PROTOCOL}://${process.env.REACT_APP_FRONTEND_API_DOMAIN}/login`.toString() ||
            "");
      }

      // An order the balance does not cover: the page says so, nothing broke.
      if (err.extensions?.code === "INSUFFICIENT_BALANCE") continue;

      bugsnagClient.notify(new Error(err.message), (event) => {
        event.severity = "warning";
        event.addMetadata("graphql", {
          code: err.extensions?.code,
          path: err.path,
          locations: err.locations,
        });
      });
    }

    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    bugsnagClient.notify(networkError as any, (event) => {
      event.severity = "error";
      event.addMetadata("graphql", { type: "network" });
    });
  }
});

// Создаем кэш с настройками для обработки BigInt
const cache = new InMemoryCache({
  typePolicies: {
    AppStoreSearchResults: {
      fields: {
        internalId: {
          read(value) {
            // При чтении данных из кэша преобразуем значение в строку
            return value ? String(value) : null;
          },
        },
      },
    },
  },
});

export const graphqlClient = new ApolloClient({
  cache,
  link: from([
    accountLink,
    errorLink,
    bigIntMiddleware,
    httpLink,
    routeAbortLink,
  ]),
  typeDefs,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
    mutate: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  },
});
